import React, { useEffect, useState } from 'react';
import { Table, Input, Button, Space, DatePicker, Select } from 'antd';
import { supabase } from '../../supabaseClient';
import dayjs from 'dayjs';

const { Option } = Select;

const bizTypeMap: Record<number, string> = {
  1: '设备产出',
  2: '个人释放',
  3: '节点释放',
  4: '提取',
  5: 'RDA算力',
};

const flowDirectionMap: Record<string, string> = {
  '+': '收入',
  '-': '支出',
};

const AssetLogList: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [bizType, setBizType] = useState<string | undefined>();
  const [flowDirection, setFlowDirection] = useState<string | undefined>();

  const fetchLogs = async () => {
    setLoading(true);
    let query = supabase.from('u_asset_logs').select('*').order('created_at', { ascending: false });
    if (userId) query = query.eq('user_id', userId);
    if (bizType) query = query.eq('biz_type', bizType);
    if (flowDirection) query = query.eq('flow_direction', flowDirection);
    if (dateRange && dateRange[0]) query = query.gte('created_at', dateRange[0] + ' 00:00:00');
    if (dateRange && dateRange[1]) query = query.lte('created_at', dateRange[1] + ' 23:59:59');
    let { data: logs, error } = await query;
    if (!error && logs && logs.length > 0) {
      let emails: Record<string, string> = {};
      if (userEmail) {
        const { data: users } = await supabase.from('u_profiles').select('user_id,email').ilike('email', `%${userEmail}%`);
        const userIds = (users || []).map(u => u.user_id);
        logs = logs.filter((l: any) => userIds.includes(l.user_id));
        (users || []).forEach(u => { emails[u.user_id] = u.email; });
      } else {
        const userIds = logs.map((l: any) => l.user_id);
        const { data: users } = await supabase.from('u_profiles').select('user_id,email').in('user_id', userIds);
        (users || []).forEach(u => { emails[u.user_id] = u.email; });
      }
      setData(logs.map((l: any) => ({ ...l, email: emails[l.user_id] || '' })));
    } else {
      setData([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line
  }, []);

  const columns = [
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 160, ellipsis: true, render: (text: string) => text ? new Date(text).toLocaleString('zh-CN', { hour12: false }) : '' },
    { title: '用户ID', dataIndex: 'user_id', key: 'user_id', width: 300, ellipsis: true },
    { title: '用户邮箱', dataIndex: 'email', key: 'email', width: 200, ellipsis: true },
    { title: '变动前金额', dataIndex: 'before_amount', key: 'before_amount', width: 120, ellipsis: true },
    { title: '变动后金额', dataIndex: 'after_amount', key: 'after_amount', width: 120, ellipsis: true },
    { title: '变动金额', dataIndex: 'amount', key: 'amount', width: 120, ellipsis: true },
    { title: '流向', dataIndex: 'flow_direction', key: 'flow_direction', width: 80, ellipsis: true, render: (v: string) => flowDirectionMap[v] || v },
    { title: '业务类型', dataIndex: 'biz_type', key: 'biz_type', width: 120, ellipsis: true, render: (v: number) => bizTypeMap[v] || v },
    { title: '备注', dataIndex: 'remark', key: 'remark', width: 200, ellipsis: true },
  ];

  return (
    <div style={{ overflowX: 'auto', background: '#fff', padding: 12 }}>
      <Space style={{ marginBottom: 16, flexWrap: 'wrap', display: 'flex' }} size={[8, 8]}>
        <Input
          style={{ width: 200 }}
          placeholder="用户ID"
          value={userId}
          onChange={e => setUserId(e.target.value)}
          allowClear
        />
        <Input
          style={{ width: 200 }}
          placeholder="邮箱"
          value={userEmail}
          onChange={e => setUserEmail(e.target.value)}
          allowClear
        />
        <Select
          allowClear
          style={{ width: 120 }}
          placeholder="业务类型"
          value={bizType}
          onChange={setBizType}
        >
          <Option value="1">设备产出</Option>
          <Option value="2">个人释放</Option>
          <Option value="3">节点释放</Option>
          <Option value="4">提取</Option>
          <Option value="5">RDA算力</Option>
        </Select>
        <Select
          allowClear
          style={{ width: 100 }}
          placeholder="流向"
          value={flowDirection}
          onChange={setFlowDirection}
        >
          <Option value="+">收入</Option>
          <Option value="-">支出</Option>
        </Select>
        <DatePicker.RangePicker
          style={{ width: 260 }}
          placeholder={['开始', '结束']}
          value={dateRange ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null}
          onChange={dates => setDateRange(
            dates && dates[0] && dates[1]
              ? [dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')]
              : null
          )}
          allowClear
        />
        <Button type="primary" onClick={fetchLogs} loading={loading}>筛选</Button>
        <Button onClick={() => { setUserId(''); setUserEmail(''); setDateRange(null); setBizType(undefined); setFlowDirection(undefined); fetchLogs(); }}>重置</Button>
      </Space>
      <Table
        rowKey="uuid"
        columns={columns}
        dataSource={data}
        loading={loading}
        bordered
        pagination={{ pageSize: 15, showSizeChanger: false, showTotal: (total) => `共 ${total} 条数据`, position: ['bottomRight'] }}
        scroll={{ x: 1400 }}
        size="small"
        style={{ minWidth: 1200 }}
      />
    </div>
  );
};

export default AssetLogList;
